/**
 * Lane C0-FACE round 2 (lab only). User, 2026-10-10: keep C0's current eyes and brows as the base (the traced
 * features of head2.ts: their size, iris, shine, lashes, the bold brows) and build variety on them. So the eyes
 * and brows here are the traced sprites (via face2.ts `fineFace`, which already makes the live variants) moved by
 * more pixel steps — lids, lash weight, flicks, thicker / longer / sharper brows — and expressions are steps too:
 * a lid coming down at the inner corner (anger), lids squeezing (pain), the lower lid pushed up (a smile), the
 * brows' inner ends dropping or rising. Every step works on each eye or brow of a sprite in every view.
 *
 * Two special pixel letters are added to a sprite's own: `@` the sprite's darkest colour (lash), `#` a lid crease
 * (skin shade). draw.ts paints them.
 */
import type { View } from '../../sprites';
import { type FineSlot, fineFace } from '../face2';
import type { FineFace, FineSprite } from '../head2';
import type { Expr } from './parts';

export const LASH = '@';
export const CREASE = '#';

type Px = { x: number; y: number; ch: string };

/** One pixel step on each feature (each eye or brow) of a sprite. */
export type Step =
  /** Lift the outer end by `n` (rising steadily from the inner end). */
  | { tilt: number }
  /** Lift only the outer corner by `n`. */
  | { corner: number }
  /** Lift the middle by `n` (an arch). */
  | { arch: number }
  /** Repeat the row `at` of the way down (taller). */
  | { addRow: number }
  /** Take out the row `at` of the way down. */
  | { dropRow: number }
  /** Repeat the column `at` of the way from the inner end (wider). */
  | { addCol: number }
  /** Move the inner end down by `n` (negative: up), the outer end staying. */
  | { inner: number }
  /** Move the whole feature up by `n`. */
  | { lift: number }
  /** Trim `n` pixels off the top of each column, weighted toward the inner end, flat, or the outer end; the new top is lash. */
  | { lidTop: number; at: 'inner' | 'flat' | 'outer' }
  /** Trim up to `n` pixels off the bottom of each column in an arc (most in the middle); the new bottom is lash. */
  | { lidBot: number; at: 'arc' | 'flat' }
  /** A lash flick of `n` pixels up and out past the outer corner. */
  | { flick: number }
  /** A second lash row over the top of the outer `from`..1 of the eye (a heavier lash). */
  | { lashUp: number }
  /** A crease line two rows over the eye (a double lid or a hooded lid). */
  | { crease: 'double' | 'hood' };

export interface Variant {
  id: string;
  label: string;
  /** The live variant (face2.ts) this starts from. */
  base: string;
  steps: readonly Step[];
}

/** Eyes: the live six, then new ones made the same way (same size, same iris and shine). */
export const EYE_VARIANTS: readonly Variant[] = [
  { id: 'ref', label: 'ต้นแบบ', base: 'ref', steps: [] },
  { id: 'phoenix', label: 'ตาหงส์', base: 'phoenix', steps: [] },
  { id: 'round', label: 'ตากลมโต', base: 'round', steps: [] },
  { id: 'narrow', label: 'ตาเรียวคม', base: 'narrow', steps: [] },
  { id: 'gentle', label: 'ตาอ่อนโยน', base: 'gentle', steps: [] },
  { id: 'fierce', label: 'ตาดุ', base: 'fierce', steps: [] },
  // Women: lash flick, a lifted outer corner.
  { id: 'doe', label: 'ตากลมหวาน (แพขนตา)', base: 'round', steps: [{ flick: 1 }] },
  { id: 'cat', label: 'ตาแมว (หางตาตวัด)', base: 'phoenix', steps: [{ flick: 2 }] },
  { id: 'lidded', label: 'ตาชั้นเดียวหวาน', base: 'gentle', steps: [{ crease: 'double' }, { flick: 1 }] },
  { id: 'cool', label: 'ตาเย็นชา', base: 'narrow', steps: [{ lidTop: 1, at: 'flat' }, { flick: 1 }] },
  // Men: heavier lash, straighter lids.
  { id: 'steady', label: 'ตาคมนิ่ง (ขนตาหนา)', base: 'ref', steps: [{ lashUp: 0.25 }] },
  { id: 'sharp', label: 'ตาคมกริบ', base: 'narrow', steps: [{ lashUp: 0.3 }, { corner: 1 }] },
  { id: 'hooded', label: 'ตาหนังตาหนา', base: 'fierce', steps: [{ crease: 'hood' }, { corner: -1 }] },
  { id: 'droopy', label: 'ตาตกใจดี', base: 'gentle', steps: [{ lashUp: 0.4 }, { corner: -1 }] },
  // Elders: a hooded, tired lid over the same eye.
  { id: 'aged', label: 'ตาผู้เฒ่า (หนังตาตก)', base: 'ref', steps: [{ lidTop: 1, at: 'outer' }, { crease: 'hood' }] },
  { id: 'agedFierce', label: 'ตาผู้เฒ่าดุ', base: 'fierce', steps: [{ crease: 'hood' }, { lashUp: 0.3 }] },
];

/** Brows: the live six, then bolder ones (bushy, long-tailed, sharp) made the same way. */
export const BROW_VARIANTS: readonly Variant[] = [
  { id: 'ref', label: 'ต้นแบบ', base: 'ref', steps: [] },
  { id: 'sword', label: 'คิ้วดาบ', base: 'sword', steps: [] },
  { id: 'arch', label: 'คิ้วโก่ง', base: 'arch', steps: [] },
  { id: 'thin', label: 'คิ้วเรียวบาง', base: 'thin', steps: [] },
  { id: 'thick', label: 'คิ้วหนา', base: 'thick', steps: [] },
  { id: 'worried', label: 'คิ้วตก', base: 'worried', steps: [] },
  { id: 'willow', label: 'คิ้วใบหลิว', base: 'thin', steps: [{ arch: 1 }, { addCol: 0.7 }] },
  { id: 'lifted', label: 'คิ้วเฉียงขึ้น', base: 'thin', steps: [{ tilt: 2 }] },
  { id: 'blade', label: 'คิ้วดาบหนา (เฉียงคม)', base: 'thick', steps: [{ tilt: 3 }] },
  { id: 'bushy', label: 'คิ้วดกหนา', base: 'thick', steps: [{ addRow: 0.5 }, { addCol: 0.5 }] },
  { id: 'knit', label: 'คิ้วขมวด', base: 'thick', steps: [{ inner: 1 }, { tilt: 1 }] },
  { id: 'kind', label: 'คิ้วใจดี', base: 'ref', steps: [{ corner: -2 }, { arch: 1 }] },
  { id: 'longTail', label: 'คิ้วยาวหางตก (ผู้เฒ่า)', base: 'thick', steps: [{ addCol: 0.8 }, { addCol: 0.9 }, { corner: -3 }] },
  { id: 'wildElder', label: 'คิ้วดกชี้ (ผู้เฒ่า)', base: 'thick', steps: [{ addRow: 0.5 }, { tilt: 3 }, { addCol: 0.85 }] },
];

/** Expression steps per slot (eyes, brows), applied after the variant's. */
const EXPR_STEPS: Record<Expr, { eyes: readonly Step[]; brows: readonly Step[] }> = {
  neutral: { eyes: [], brows: [] },
  smile: { eyes: [{ lidBot: 2, at: 'arc' }], brows: [{ lift: 1 }] },
  talk: { eyes: [], brows: [{ lift: 1 }] },
  angry: { eyes: [{ lidTop: 2, at: 'inner' }], brows: [{ inner: 2 }] },
  shout: { eyes: [{ addRow: 0.5 }, { lidTop: 2, at: 'inner' }], brows: [{ inner: 3 }] },
  hurt: { eyes: [{ lidTop: 2, at: 'outer' }, { lidBot: 1, at: 'flat' }], brows: [{ inner: -2 }] },
  blink: { eyes: [], brows: [] },
};

// ── pixels (as face2.ts) ──

function pixels(sp: FineSprite): Px[] {
  const out: Px[] = [];
  sp.rows.forEach((row, r) => {
    for (let c = 0; c < row.length; c++) if (row[c] !== '.') out.push({ x: sp.x + c, y: sp.y + r, ch: row[c]! });
  });
  return out;
}

function sprite(px: readonly Px[], palette: readonly string[]): FineSprite {
  if (!px.length) return { x: 0, y: 0, palette, rows: [] };
  const x0 = Math.min(...px.map((p) => p.x));
  const y0 = Math.min(...px.map((p) => p.y));
  const w = Math.max(...px.map((p) => p.x)) - x0 + 1;
  const h = Math.max(...px.map((p) => p.y)) - y0 + 1;
  const rows = Array.from({ length: h }, () => Array<string>(w).fill('.'));
  for (const p of px) rows[p.y - y0]![p.x - x0] = p.ch;
  return { x: x0, y: y0, palette, rows: rows.map((r) => r.join('')) };
}

/** Runs of columns apart from each other (two eyes / two brows), each with its outer side (−1 left, 1 right). */
function features(px: readonly Px[], view: View): { px: Px[]; out: number }[] {
  const xs = [...new Set(px.map((p) => p.x))].sort((a, b) => a - b);
  const runs: number[][] = [];
  for (const x of xs) {
    const run = runs[runs.length - 1];
    if (run && x - run[run.length - 1]! <= 2) run.push(x);
    else runs.push([x]);
  }
  return runs.map((run, i) => ({
    px: px.filter((p) => p.x >= run[0]! && p.x <= run[run.length - 1]!),
    out: runs.length > 1 ? (i === 0 ? -1 : 1) : view === 'side' ? -1 : 1,
  }));
}

/** Columns of a feature, each sorted top to bottom. */
function columns(px: readonly Px[]): Map<number, Px[]> {
  const cols = new Map<number, Px[]>();
  for (const p of px) {
    const c = cols.get(p.x) ?? [];
    c.push(p);
    cols.set(p.x, c);
  }
  for (const c of cols.values()) c.sort((a, b) => a.y - b.y);
  return cols;
}

function step(f: { px: Px[]; out: number }, s: Step): Px[] {
  if (!f.px.length) return f.px;
  const xs = f.px.map((p) => p.x);
  const ys = f.px.map((p) => p.y);
  const [x0, x1, y0, y1] = [Math.min(...xs), Math.max(...xs), Math.min(...ys), Math.max(...ys)];
  const w = Math.max(1, x1 - x0);
  // 0 at the inner end, 1 at the outer.
  const along = (x: number) => (f.out > 0 ? (x - x0) / w : (x1 - x) / w);
  if ('tilt' in s) return f.px.map((p) => ({ ...p, y: p.y - Math.round(s.tilt * along(p.x)) }));
  if ('corner' in s)
    return f.px.map((p) => ({ ...p, y: p.y - Math.round(s.corner * Math.max(0, (along(p.x) - 0.6) / 0.4)) }));
  if ('arch' in s) return f.px.map((p) => ({ ...p, y: p.y - Math.round(s.arch * Math.sin(Math.PI * ((p.x - x0) / w))) }));
  if ('inner' in s) return f.px.map((p) => ({ ...p, y: p.y + Math.round(s.inner * Math.max(0, 1 - along(p.x) * 1.6)) }));
  if ('lift' in s) return f.px.map((p) => ({ ...p, y: p.y - s.lift }));
  if ('addRow' in s || 'dropRow' in s) {
    const at = y0 + Math.round(('addRow' in s ? s.addRow : s.dropRow) * (y1 - y0));
    if ('dropRow' in s)
      return y1 === y0 ? f.px : f.px.filter((p) => p.y !== at).map((p) => (p.y > at ? { ...p, y: p.y - 1 } : p));
    return f.px.flatMap((p) => (p.y > at ? [{ ...p, y: p.y + 1 }] : p.y === at ? [p, { ...p, y: p.y + 1 }] : [p]));
  }
  if ('addCol' in s) {
    const dir = f.out || 1;
    const at = Math.round(dir > 0 ? x0 + s.addCol * (x1 - x0) : x1 - s.addCol * (x1 - x0));
    return f.px.flatMap((p) =>
      (p.x - at) * dir > 0 ? [{ ...p, x: p.x + dir }] : p.x === at ? [p, { ...p, x: p.x + dir }] : [p],
    );
  }
  const cols = columns(f.px);
  if ('lidTop' in s || 'lidBot' in s) {
    const out: Px[] = [];
    for (const [x, col] of cols) {
      const a = along(x);
      let n: number;
      if ('lidTop' in s) n = Math.round(s.lidTop * (s.at === 'flat' ? 1 : s.at === 'inner' ? Math.max(0, 1 - a * 1.4) : 0.5 + a * 0.6));
      else n = Math.round(s.lidBot * (s.at === 'flat' ? 1 : Math.sin(Math.PI * a)));
      // Keep at least one pixel: the lid line itself.
      n = Math.min(n, col.length - 1);
      const kept = 'lidTop' in s ? col.slice(n) : col.slice(0, col.length - n);
      if (n > 0) {
        const edge = 'lidTop' in s ? 0 : kept.length - 1;
        kept[edge] = { ...kept[edge]!, ch: LASH };
      }
      out.push(...kept);
    }
    return out;
  }
  if ('flick' in s) {
    const xo = f.out > 0 ? x1 : x0;
    const top = cols.get(xo)![0]!;
    const out = [...f.px];
    for (let i = 1; i <= s.flick; i++) out.push({ x: xo + f.out * i, y: top.y - i, ch: LASH });
    return out;
  }
  if ('lashUp' in s) {
    const out = [...f.px];
    for (const [x, col] of cols) if (along(x) >= s.lashUp && along(x) < 1) out.push({ x, y: col[0]!.y - 1, ch: LASH });
    return out;
  }
  // Crease: two rows over each column's top, the inner and outer ends left clear.
  const out = [...f.px];
  for (const [x, col] of cols) {
    const a = along(x);
    if (a < 0.12 || a > (s.crease === 'hood' ? 0.95 : 0.8)) continue;
    out.push({ x, y: col[0]!.y - (s.crease === 'hood' ? 1 : 2), ch: CREASE });
  }
  // A crease pixel put over a lash pixel keeps the lash.
  const seen = new Set(f.px.map((p) => `${p.x},${p.y}`));
  return out.filter((p, i) => i < f.px.length || !seen.has(`${p.x},${p.y}`));
}

function vary(sp: FineSprite, steps: readonly Step[], view: View): FineSprite {
  if (!steps.length) return sp;
  const parts = features(pixels(sp), view).map((f) => {
    let px = f.px;
    for (const s of steps) px = step({ px, out: f.out }, s);
    return px;
  });
  return sprite(parts.flat(), sp.palette);
}

const CACHE = new Map<string, FineFace>();

const byId = (list: readonly Variant[], id: string) => list.find((v) => v.id === id) ?? list[0]!;

/** The traced eyes (open or shut) and brows for a view, variant and expression; undefined where the view has none. */
export function fineFeatures(view: View, eyes: string, brows: string, expr: Expr): FineFace | undefined {
  const key = `${view}:${eyes}:${brows}:${expr}`;
  const hit = CACHE.get(key);
  if (hit) return hit;
  const e = byId(EYE_VARIANTS, eyes);
  const b = byId(BROW_VARIANTS, brows);
  const ids = (slot: FineSlot, id: string): Record<FineSlot, string> => ({
    eyes: 'ref',
    brows: 'ref',
    nose: 'ref',
    mouth: 'ref',
    [slot]: id,
  });
  const fe = fineFace(view, ids('eyes', e.base));
  const fb = fineFace(view, ids('brows', b.base));
  if (!fe && !fb) return undefined;
  const x = EXPR_STEPS[expr];
  // A shut eye keeps only the steps that keep a line a line.
  const lineSteps = e.steps.filter((s) => 'tilt' in s || 'corner' in s || 'addCol' in s);
  const face: FineFace = {
    ...(fe?.eyes && { eyes: vary(fe.eyes, [...e.steps, ...x.eyes], view) }),
    ...(fe?.closed && { closed: vary(fe.closed, lineSteps, view) }),
    ...(fb?.brows && { brows: vary(fb.brows, [...b.steps, ...x.brows], view) }),
  };
  CACHE.set(key, face);
  return face;
}
